import { defineComponent as _defineComponent } from 'vue'
import liveChatConfig from './data/config';
import { SitecoreProps } from '@/interfaces/pages/sitecore-props.interface';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useLiveChatStore, useJeopardyStore } from '@/store';

const COOKIE_NAME = 'LiveChat_SessionId';
const COOKIE_CATEGORY_NAME = 'LiveChat_Category';
const ACTIVE_WIDGET_COOKIE = 'LiveChat_ActiveWidget';

export default /*@__PURE__*/_defineComponent({
  __name: 'LiveChat',
  props: {
    rendering: {},
    fields: {},
    params: {}
  },
  setup(__props: any) {

const store = useLiveChatStore();
const jpStore = useJeopardyStore();
const props = __props;
const pageChatEnabled = !!(props.fields?.Enabled as Field)?.value;

let chatStarted = false;
let COOKIE_DOMAIN: string | null = (liveChatConfig as any).livechat?.domain || null; // set default here as precaution

onMounted(() => {
  (window as any).getAuthToken = getLiveChatSession;
  nextTick(() => init());
});

onUnmounted(() => {
  window.removeEventListener('lcw:ready', handleLivechatReadyAuthEvent);
  window.removeEventListener('lcw:error', handleLivechatErrorEvent);
  window.removeEventListener('lcw:chatQueued', handleLivechatStartChatEvent);
  window.removeEventListener('lcw:closeChat', handleLivechatCloseChatEvent);
  window.removeEventListener('lcw:chatRetrieved', handleLivechatChatRetrievedEvent);
  delete (window as any).getAuthToken;
  store.setLcwReady(false);
});

watch(
  () => store.liveChatEnabled,
  async (newValue) => {
    if (newValue && shouldShowWidget() && !chatStarted) {
      await startChat();
    }
  }
);

watch(
  () => [jpStore.liveChatDisabled, jpStore.showJeopardy],
  () => {
    const container = document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window');
    if (!container) {
      return;
    }

    if (jpStore.liveChatDisabled && jpStore.showJeopardy && !store.hasActiveSession) {
      container.style.display = 'none';
    } else {
      container.style.display = '';
    }
  }
);

function shouldShowWidget(): boolean {
  const activeWidget = getActiveWidget();

  // RACQ is globally active -> show RACQ widget regardless of page config
  if (activeWidget === 'racq_lcw') {
    return true;
  }

  // Jeopardy has live chat disabled and there is no ongoing session -> hide RACQ widget
  if (jpStore.liveChatDisabled && jpStore.showJeopardy && !store.hasActiveSession) {
    debug('liveChatDisabled jeopardy active — suppressing Widget racq_lcw');
    return false;
  }

  // Other widget is globally active -> hide RACQ widget entirely
  if (activeWidget === 'other_lcw') {
    debug('Widget other_lcw is active — suppressing Widget racq_lcw');
    return false;
  }

  // Nothing global -> fall back to per-page config
  return pageChatEnabled;
}

async function init() {
  store.setHasActiveSession(!!getSession());
  if (!shouldShowWidget()) return;
  // Register event listeners BEFORE loading the script to avoid missing the lcw:ready event
  window.addEventListener('lcw:ready', handleLivechatReadyAuthEvent);
  window.addEventListener('lcw:error', handleLivechatErrorEvent);
  window.addEventListener('lcw:chatQueued', handleLivechatStartChatEvent);
  window.addEventListener('lcw:closeChat', handleLivechatCloseChatEvent);
  window.addEventListener('lcw:chatRetrieved', handleLivechatChatRetrievedEvent);
  if (store.liveChatEnabled || getSession()) {
    await startChat();
  }
}

async function startChat() {
  const config = {
    ...liveChatConfig.livechat,
    widgetId: (props.fields!.chatConfiguration as any)?.livechat?.appId || liveChatConfig.livechat.appId,
    appId: (props.fields!.chatConfiguration as any)?.livechat?.appId || liveChatConfig.livechat.appId,
    orgId: (props.fields!.chatConfiguration as any)?.livechat?.orgId || liveChatConfig.livechat.orgId,
    orgUrl: (props.fields!.chatConfiguration as any)?.livechat?.orgUrl || liveChatConfig.livechat.orgUrl,
    domain: (props.fields!.chatConfiguration as any)?.livechat?.domain || liveChatConfig.livechat.domain,
  };
  COOKIE_DOMAIN = config.domain;
  store.startChat(config);
  chatStarted = true;
  const isOutOfHours = await store.checkOutOfHours(config);
  jpStore.setChatEnabled(!isOutOfHours);
}

async function getLiveChatSession(callback: (token: string | null) => void) {
  try {
    const storedSessionId = getSession();
    if (storedSessionId) {
      debug('found stored session id', storedSessionId);
    }

    // Get the category, prefer props -> config -> cookie
    const category =
      (props.fields!.chatConfiguration as any)?.livechat?.category ||
      liveChatConfig.livechat.category ||
      getStoredCategory();

    setStoredCategory(category);
    const res: any = await store.getLiveChatSessionData(category, storedSessionId || undefined);
    debug('session data', res);
    const token = res.token as string;
    const sessionId = res.sessionId as string;
    if (sessionId) {
      setSession(sessionId);
      debug('cookie updated', sessionId);
    }
    if (!token) {
      console.error('[Live Chat]', 'received empty token from session data');
      clearSession();
      callback(null);
      return;
    }
    callback(token);
  } catch (e) {
    console.error('[Live Chat]', 'error retrieving session data', e);
    clearSession();
    callback(null);
  }
}

function getSession(): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + COOKIE_NAME + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}
function setSession(value: string) {
  if (!COOKIE_DOMAIN) return;
  const expires = new Date();
  expires.setHours(expires.getHours() + 2);
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(value)}; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=${expires.toUTCString()}; ` +
    `Secure; ` +
    `SameSite=Lax;`;

  // RACQ livechat has a session -> RACQ widget owns the active slot
  setActiveWidget('racq_lcw');
  store.setHasActiveSession(true);
}

function getStoredCategory(): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + COOKIE_CATEGORY_NAME + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}
function setStoredCategory(value: string) {
  if (!COOKIE_DOMAIN) return;
  const expires = new Date();
  expires.setHours(expires.getHours() + 2);
  document.cookie =
    `${COOKIE_CATEGORY_NAME}=${encodeURIComponent(value)}; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=${expires.toUTCString()}; ` +
    `Secure; ` +
    `SameSite=Lax;`;
}

function getActiveWidget() {
  const match = document.cookie.match(new RegExp('(^| )' + ACTIVE_WIDGET_COOKIE + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}
function setActiveWidget(widgetId: string) {
  if (!COOKIE_DOMAIN) return;
  const expires = new Date();
  expires.setHours(expires.getHours() + 2);
  document.cookie =
    `${ACTIVE_WIDGET_COOKIE}=${encodeURIComponent(widgetId)}; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=${expires.toUTCString()}; ` +
    `Secure; ` +
    `SameSite=Lax;`;
}
function clearActiveWidget() {
  if (!COOKIE_DOMAIN) return;
  document.cookie =
    `${ACTIVE_WIDGET_COOKIE}=; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
    `Secure; ` +
    `SameSite=Lax;`;
}

function clearSession() {
  if (!COOKIE_DOMAIN) return;
  // Clear session cookie
  document.cookie =
    `${COOKIE_NAME}=; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
    `Secure; ` +
    `SameSite=Lax;`;

  // Clear category cookie
  document.cookie =
    `${COOKIE_CATEGORY_NAME}=; ` +
    `domain=${COOKIE_DOMAIN}; ` +
    `path=/; ` +
    `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
    `Secure; ` +
    `SameSite=Lax;`;

  // Clear the active slot
  clearActiveWidget();
  store.setHasActiveSession(false);

  debug('cookie cleared');
}

function handleLivechatReadyAuthEvent() {
  debug('ready event');
  Microsoft.Omnichannel.LiveChatWidget.SDK.setAuthTokenProvider(getLiveChatSession);
  store.setLcwReady(true);
}

function handleLivechatErrorEvent(errorEvent: any) {
  console.error('[Live Chat] - error event', errorEvent);
  clearSession();
}

function handleLivechatStartChatEvent(event: any) {
  debug('start chat event', event);
}

function handleLivechatChatRetrievedEvent(event: any) {
  debug('chat retrieved event', event);
}

function handleLivechatCloseChatEvent(event: any) {
  debug('close chat event', event);
  clearSession();
}

function debug(...args: any[]) {
  const debugMode = (props.fields!.chatConfiguration as any)?.main?.debug;
  if (debugMode) {
    console.debug('[Live Chat]', ...args);
  }
}

return () => {}
}

})